/**
 * SourceAttribution Component - Display source cards for search results
 * TASK-270: Unified Search Interface
 */

import React from 'react';
import {
  Database,
  Mail,
  TrendingUp,
  Newspaper,
  FileText,
  ExternalLink,
  Clock,
  Calendar,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { SourceType, SourceAttribution as SourceAttributionType } from '../../types/search.types';

const sourceConfig: Record<SourceType, { icon: typeof Database; label: string; color: string }> = {
  database: { icon: Database, label: 'Database', color: 'text-purple-600 bg-purple-50' },
  email: { icon: Mail, label: 'Email', color: 'text-blue-600 bg-blue-50' },
  market: { icon: TrendingUp, label: 'Market Data', color: 'text-green-600 bg-green-50' },
  news: { icon: Newspaper, label: 'News', color: 'text-orange-600 bg-orange-50' },
  document: { icon: FileText, label: 'Document', color: 'text-gray-600 bg-gray-50' },
  calendar: { icon: Calendar, label: 'Calendar', color: 'text-indigo-600 bg-indigo-50' },
  portfolio_alert: { icon: AlertTriangle, label: 'Portfolio Alert', color: 'text-red-600 bg-red-50' },
  // TASK-605: client knowledge-graph facts, deep-linked to the client's DNA tab.
  kg_fact: { icon: Sparkles, label: 'Client fact', color: 'text-teal-600 bg-teal-50' },
};

interface SourceAttributionProps {
  source: SourceAttributionType;
  onClick?: () => void;
  compact?: boolean;
  className?: string;
}

export function SourceAttribution({
  source,
  onClick,
  compact = false,
  className,
}: SourceAttributionProps) {
  const config = sourceConfig[source.sourceType] || sourceConfig.document;
  const Icon = config.icon;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      });
    } catch {
      return null;
    }
  };

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all',
          config.color,
          'hover:ring-1 hover:ring-gray-300',
          className
        )}
      >
        <Icon className="h-3 w-3" />
        <span className="max-w-[100px] truncate">{source.title || config.label}</span>
      </button>
    );
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        'group border rounded-lg p-3 transition-all cursor-pointer',
        'bg-white hover:border-gray-300 hover:shadow-sm',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={cn('p-1.5 rounded', config.color)}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 line-clamp-1">
              {source.title || config.label}
            </div>
            <div className="text-xs text-gray-500">{config.label}</div>
          </div>
        </div>
        {source.link && (
          <ExternalLink className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>

      {/* Excerpt */}
      <p className="mt-2 text-sm text-gray-600 line-clamp-2">{source.excerpt}</p>

      {/* Footer */}
      <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span>{formatDate(source.timestamp) || 'Recent'}</span>
        </div>
        <div className="flex items-center gap-1">
          <span>Relevance:</span>
          <span className="font-medium text-gray-600">
            {Math.round(source.relevanceScore * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}

interface SourceListProps {
  sources: SourceAttributionType[];
  onSourceClick?: (source: SourceAttributionType) => void;
  maxVisible?: number;
  className?: string;
}

export function SourceList({
  sources,
  onSourceClick,
  maxVisible = 3,
  className,
}: SourceListProps) {
  const [expanded, setExpanded] = React.useState(false);

  if (!sources.length) return null;

  const visibleSources = expanded ? sources : sources.slice(0, maxVisible);
  const hiddenCount = sources.length - maxVisible;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Sources ({sources.length})</h4>
        {hiddenCount > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          </button>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {visibleSources.map((source, index) => (
          <SourceAttribution
            key={`${source.sourceId}-${index}`}
            source={source}
            onClick={() => onSourceClick?.(source)}
          />
        ))}
      </div>
    </div>
  );
}

export default SourceAttribution;
